(() => {
  // Produktmål er høyde x bredde. Alle beregninger gjøres i millimeter.
  const MODULES = { 50: [45, 70, 80], 70: [50, 80], 80: [50, 70] };
  const LABELS = { front: 'Front', left: 'Venstre side', right: 'Høyre side' };
  const END_LABELS = { wall: 'Veggstender', standard: 'Standardstender', 'corner-standard-middle': 'Felles standardstender i hjørne (midt)', 'corner-standard-edge': 'Felles standardstender i hjørne (kant)', 'corner-wall': 'Felles veggstender i hjørne' };
  const MM = 10, GLASS_OVERLAP = 18, WALL = 30, STANDARD = 15, STANDARD_END = 14;

  function cm(mm) {
    return (mm / 10).toFixed(2).replace('.', ',');
  }

  function fitText(rest) {
    if (rest === 0) return 'Passer nøyaktig';
    return rest > 0 ? `${cm(rest)} cm ledig plass` : `${cm(Math.abs(rest))} cm for stor`;
  }

  function combinations(widths, maxCount = 30) {
    const out = [];
    function walk(start, left, items) {
      if (items.length) out.push([...items]);
      if (!left) return;
      for (let i = start; i < widths.length; i += 1) {
        items.push(widths[i]); walk(i, left - 1, items); items.pop();
      }
    }
    walk(0, maxCount, []);
    return out;
  }

  function endCost(type) {
    if (type === 'corner-standard-middle') return 10;
    if (type === 'corner-standard-edge') return 40;
    if (type === 'corner-wall') return WALL;
    return type === 'standard' ? STANDARD_END : WALL;
  }

  function solve(lengthMm, height, leftEnd, rightEnd, cornerDeduction) {
    let best = null;
    for (const modules of combinations(MODULES[height])) {
      const postWidth = endCost(leftEnd) + endCost(rightEnd) + (modules.length - 1) * STANDARD;
      const available = lengthMm - postWidth;
      const glassWidth = modules.reduce((sum, width) => sum + width * MM - GLASS_OVERLAP, 0);
      const used = postWidth + glassWidth;
      const rest = lengthMm - used;
      const score = Math.abs(rest) + (rest < 0 ? 5000 : 0) + modules.length * .001;
      if (!best || score < best.score) {
        const lastModuleWidth = modules[modules.length - 1] * MM;
        best = {
          modules,
          used,
          available,
          rest,
          score,
          specialGlass: rest === 0 ? null : {
            replaces: lastModuleWidth,
            width: lastModuleWidth + rest
          }
        };
      }
    }
    return best;
  }

  function solveAuto(lengthMm, leftEnd, rightEnd, cornerDeduction) {
    return Object.keys(MODULES).map(Number)
      .map(height => ({ height, result: solve(lengthMm, height, leftEnd, rightEnd, cornerDeduction) }))
      .sort((a, b) => a.result.score - b.result.score || a.height - b.height)[0];
  }

  function connections(side, sides) {
    if (side === 'front') return { left: sides.includes('left'), right: sides.includes('right') };
    if (side === 'left') return { left: false, right: sides.includes('front') };
    return { left: sides.includes('front'), right: false };
  }

  function cornerKey(side, position) {
    if (side === 'front') return position;
    if (side === 'left' && position === 'right') return 'left';
    if (side === 'right' && position === 'left') return 'right';
    return null;
  }

  function endpointLabel(side, position) {
    if (side === 'front') return position === 'left' ? 'Venstre ende' : 'Høyre ende';
    if (side === 'left') return position === 'left' ? 'Ytre ende' : 'Ende mot front';
    return position === 'left' ? 'Ende mot front' : 'Ytre ende';
  }

  function render3D(sides, calculated = []) {
    const counts = Object.fromEntries(calculated.map(item => [item.side, item.result.modules.length]));
    const railings = [], lines = [], labels = [];
    const sideGeometry = {
      front: { a: [170, 85], b: [630, 85], depth: [0, 100] },
      left: { a: [170, 85], b: [55, 10], depth: [0, 100] },
      right: { a: [630, 85], b: [745, 10], depth: [0, 100] }
    };
    const order = ['left', 'front', 'right'];
    order.filter(side => sides.includes(side)).forEach(side => {
      const geometry = sideGeometry[side], paneCount = Math.max(1, counts[side] || 2), [ax, ay] = geometry.a, [bx, by] = geometry.b, [, dy] = geometry.depth;
      const dx = (bx - ax) / paneCount, sy = (by - ay) / paneCount;
      railings.push(`<polygon points="${ax},${ay + dy} ${bx},${by + dy} ${bx},${by + dy + 92} ${ax},${ay + dy + 92}" fill="${side === 'front' ? '#d7ddd9' : '#c4ceca'}" stroke="#74827d" stroke-width="1.5"/>`);
      railings.push(`<line x1="${ax}" y1="${ay + dy}" x2="${bx}" y2="${by + dy}" stroke="#17342f" stroke-width="9" stroke-linecap="round"/>`);
      for (let index = 0; index < paneCount; index += 1) {
        const x1 = ax + dx * index, y1 = ay + sy * index, x2 = ax + dx * (index + 1), y2 = ay + sy * (index + 1);
        lines.push(`<polygon points="${x1},${y1} ${x2},${y2} ${x2},${y2 + dy} ${x1},${y1 + dy}" fill="rgba(193,232,238,.58)" stroke="#75a7ad" stroke-width="1.5"/>`);
      }
      for (let index = 0; index <= paneCount; index += 1) {
        const x = ax + dx * index, y = ay + sy * index;
        lines.push(`<line x1="${x}" y1="${y - 3}" x2="${x}" y2="${y + dy + 4}" stroke="#17342f" stroke-width="5" stroke-linecap="round"/>`);
      }
      lines.push(`<line x1="${ax}" y1="${ay + dy}" x2="${bx}" y2="${by + dy}" stroke="#17342f" stroke-width="7" stroke-linecap="round"/>`);
      labels.push(`<text x="${(ax + bx) / 2}" y="${Math.max(21, (ay + by) / 2 - 14)}" text-anchor="middle" fill="#17342f" font-family="Arial,sans-serif" font-size="18" font-weight="700">${LABELS[side]} · ${paneCount} felt</text>`);
    });
    const connected = sides.includes('front') && (sides.includes('left') || sides.includes('right'));
    const cornerDots = connected ? `${sides.includes('left') ? '<circle cx="170" cy="185" r="9" fill="#d79836"/><text x="170" y="168" text-anchor="middle" fill="#785318" font-family="Arial,sans-serif" font-size="15" font-weight="700">Felles hjørne</text>' : ''}${sides.includes('right') ? '<circle cx="630" cy="185" r="9" fill="#d79836"/><text x="630" y="168" text-anchor="middle" fill="#785318" font-family="Arial,sans-serif" font-size="15" font-weight="700">Felles hjørne</text>' : ''}` : '';
    const cornerAngles = connected ? `${sides.includes('left') ? '<path d="M194 85 A24 24 0 0 0 179 67" fill="none" stroke="#d79836" stroke-width="3"/><text x="198" y="62" fill="#785318" font-family="Arial,sans-serif" font-size="18" font-weight="700">90°</text>' : ''}${sides.includes('right') ? '<path d="M606 85 A24 24 0 0 1 621 67" fill="none" stroke="#d79836" stroke-width="3"/><text x="576" y="62" fill="#785318" font-family="Arial,sans-serif" font-size="18" font-weight="700">90°</text>' : ''}` : '';
    const railingDimension = sides.includes('front') ? '<line x1="665" y1="185" x2="665" y2="277" stroke="#52645f" stroke-width="2"/><line x1="658" y1="185" x2="672" y2="185" stroke="#52645f" stroke-width="2"/><line x1="658" y1="277" x2="672" y2="277" stroke="#52645f" stroke-width="2"/><text x="682" y="234" fill="#17342f" font-family="Arial,sans-serif" font-size="17" font-weight="700">Rekkverk 1,0 m</text>' : '';
    const viewDescription = connected ? '3D-visning av glass med 90 graders hjørne på et 1 meter høyt rekkverk' : '3D-visning av frontglass på et 1 meter høyt rekkverk';
    const caption = connected ? 'Sideglassene er montert 90° på fronten. Glass og stendere står oppå et 1,0 m høyt rekkverk.' : 'Frontglass og stendere er montert oppå et 1,0 m høyt rekkverk.';
    return `<div class="eg__3d"><svg viewBox="0 0 800 305" role="img" aria-label="${viewDescription}" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="eg-floor" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#eef4f1"/><stop offset="1" stop-color="#ffffff"/></linearGradient></defs><path d="M20 292 L170 277 L630 277 L780 292 Z" fill="url(#eg-floor)" stroke="#d7e1dd"/>${railings.join('')}${lines.join('')}${cornerDots}${cornerAngles}${labels.join('')}${railingDimension}</svg><p class="eg__3d-caption">${caption}${calculated.length ? ' Antall glassfelt følger beregningen.' : ''}</p></div>`;
  }

  function diagram(d, corner) {
    const widths = d.result.modules, n = widths.length, x0 = 55, x1 = 745, top = 62, bottom = 185, full = x1 - x0;
    const units = widths.reduce((sum, width) => sum + width * MM, 0), gap = 8, usable = full - gap * (n - 1), scale = usable / units;
    let x = x0;
    const panes = [];
    widths.forEach((width, index) => {
      const paneWidth = width * MM * scale;
      panes.push(`<rect x="${x.toFixed(1)}" y="${top}" width="${paneWidth.toFixed(1)}" height="${bottom - top}" rx="2" class="eg-svg-pane" fill="#e2f1f2" stroke="#6e9fa1" stroke-width="1.5"/><line x1="${x.toFixed(1)}" y1="${top - 5}" x2="${x.toFixed(1)}" y2="${bottom + 5}" class="eg-svg-post" stroke="#17342f" stroke-width="7"/><text x="${(x + paneWidth / 2).toFixed(1)}" y="118" text-anchor="middle" class="eg-svg-main" fill="#17342f" font-family="Arial, sans-serif" font-size="14" font-weight="600">${d.height} x ${width} cm</text><text x="${(x + paneWidth / 2).toFixed(1)}" y="139" text-anchor="middle" class="eg-svg-small" fill="#52645f" font-family="Arial, sans-serif" font-size="12">lysmål ${width * MM - GLASS_OVERLAP} mm</text>`);
      x += paneWidth;
      if (index < n - 1) x += gap;
    });
    panes.push(`<line x1="${x1}" y1="${top - 5}" x2="${x1}" y2="${bottom + 5}" class="eg-svg-post" stroke="#17342f" stroke-width="7"/>`);
    const endText = type => END_LABELS[type];
    return `<div class="eg__diagram"><div class="eg__diagram-title"><strong>${LABELS[d.side]}</strong><span>${d.automatic ? 'Auto: anbefalt høyde ' : 'Glasshøyde '}${d.height} cm · stenderhøyde ${d.height} cm</span></div><svg viewBox="0 0 800 245" role="img" aria-label="Målsatt diagram for ${LABELS[d.side]}" xmlns="http://www.w3.org/2000/svg"><defs><marker id="arrow-${d.side}" markerWidth="7" markerHeight="7" refX="3.5" refY="3.5" orient="auto-start-reverse"><path d="M0,0 L7,3.5 L0,7 z" class="eg-svg-arrow" fill="#17342f"/></marker></defs><line x1="${x0}" y1="30" x2="${x1}" y2="30" class="eg-svg-dim" stroke="#17342f" stroke-width="1.5" marker-start="url(#arrow-${d.side})" marker-end="url(#arrow-${d.side})"/><line x1="${x0}" y1="36" x2="${x0}" y2="55" class="eg-svg-guide" stroke="#83918d" stroke-width="1" stroke-dasharray="3 3"/><line x1="${x1}" y1="36" x2="${x1}" y2="55" class="eg-svg-guide" stroke="#83918d" stroke-width="1" stroke-dasharray="3 3"/><rect x="300" y="14" width="200" height="28" class="eg-svg-label" fill="#ffffff"/><text x="400" y="34" text-anchor="middle" class="eg-svg-main" fill="#17342f" font-family="Arial, sans-serif" font-size="14" font-weight="600">Plass kunden har: ${cm(d.length)} cm</text>${panes.join('')}<text x="${x0}" y="218" class="eg-svg-small" fill="#52645f" font-family="Arial, sans-serif" font-size="12">${endText(d.ends.left)}</text><text x="${x1}" y="218" text-anchor="end" class="eg-svg-small" fill="#52645f" font-family="Arial, sans-serif" font-size="12">${endText(d.ends.right)}</text><text x="400" y="238" text-anchor="middle" class="eg-svg-rest" fill="#17342f" font-family="Arial, sans-serif" font-size="13" font-weight="600">Total plass brukt med glass og stendere: ${cm(d.result.used)} cm · ${fitText(d.result.rest)}</text></svg></div>`;
  }

  document.querySelectorAll('[data-ethus-glass]').forEach(root => {
    const form = root.querySelector('.eg__form');
    const panels = root.querySelector('[data-side-panels]');
    const picks = [...root.querySelectorAll('input[name="side"]')];
    const template = document.getElementById(root.id.replace('EthusGlass', 'EthusGlassPanel'));
    const selected = () => picks.filter(item => item.checked).map(item => item.value);

    function endControl(side, position, connected, value = 'standard') {
      const label = endpointLabel(side, position);
      if (connected) return `<div class="eg__field"><span>${label}</span><div class="eg__end-fixed"><span>Felles stender i 90° hjørne<small>Velg standard- eller veggstender under toppbord og hjørneløsning</small></span></div><span class="eg__corner-badge">Sammenkoblet</span></div>`;
      return `<label class="eg__field"><span>${label}</span><select name="end-${position}"><option value="standard"${value === 'standard' ? ' selected' : ''}>Standardstender</option><option value="wall"${value === 'wall' ? ' selected' : ''}>Veggstender</option></select></label>`;
    }

    function renderCornerControls() {
      const sides = selected(), holder = root.querySelector('[data-corner-controls]'), topBoard = form.elements.topBoard.value;
      const previous = {};
      holder.querySelectorAll('[data-corner]').forEach(item => { previous[item.dataset.corner] = { post: item.querySelector('[name^="corner-post-"]')?.value, mount: item.querySelector('[name^="corner-mount-"]')?.value }; });
      const corners = ['left', 'right'].filter(key => sides.includes('front') && sides.includes(key));
      root.querySelector('[data-corner-card]').hidden = !corners.length;
      root.querySelectorAll('[data-topboard-picture]').forEach(item => item.classList.toggle('is-active', item.dataset.topboardPicture === topBoard));
      holder.innerHTML = corners.map(key => {
        const post = previous[key]?.post || 'standard', mount = topBoard === 'curved' ? 'middle' : (previous[key]?.mount || 'middle');
        const placementLocked = topBoard === 'curved' || post === 'wall';
        const placementHelp = post === 'wall' ? '<small>Plassering i midten eller på kanten gjelder ikke for veggstender.</small>' : topBoard === 'curved' ? '<small>Buet toppbord kan bare monteres i midten.</small>' : '';
        return `<div class="eg__corner-control" data-corner="${key}"><h4>${key === 'left' ? 'Venstre' : 'Høyre'} hjørne</h4><div class="eg__grid"><label class="eg__field"><span>Stender i hjørnet</span><select name="corner-post-${key}"><option value="standard"${post === 'standard' ? ' selected' : ''}>Standardstender</option><option value="wall"${post === 'wall' ? ' selected' : ''}>Veggstender</option></select></label><label class="eg__field"><span>Plassering</span><select name="corner-mount-${key}"${placementLocked ? ' disabled' : ''}><option value="middle"${mount === 'middle' ? ' selected' : ''}>I midten</option><option value="edge"${mount === 'edge' ? ' selected' : ''}>På kanten</option></select>${placementHelp}</label></div></div>`;
      }).join('');
      holder.querySelectorAll('select').forEach(control => control.addEventListener('change', () => control.name.startsWith('corner-post-') ? renderCornerControls() : draw()));
    }

    function cornerEnd(key) {
      const item = root.querySelector(`[data-corner="${key}"]`), post = item.querySelector(`[name="corner-post-${key}"]`).value;
      if (post === 'wall') return 'corner-wall';
      const mount = form.elements.topBoard.value === 'curved' ? 'middle' : item.querySelector(`[name="corner-mount-${key}"]`).value;
      return `corner-standard-${mount}`;
    }

    function renderPanels() {
      const old = {};
      panels.querySelectorAll('[data-side]').forEach(panel => {
        old[panel.dataset.side] = {
          length: panel.querySelector('[name="length"]').value,
          height: panel.querySelector('[name="height"]').value,
          left: panel.querySelector('[name="end-left"]')?.value || 'standard',
          right: panel.querySelector('[name="end-right"]')?.value || 'standard'
        };
      });
      const sides = selected();
      panels.innerHTML = '';
      sides.forEach(side => {
        const node = template.content.cloneNode(true), field = node.querySelector('fieldset');
        field.dataset.side = side;
        node.querySelector('[data-title]').textContent = `2. Mål for ${LABELS[side].toLowerCase()}`;
        const height = node.querySelector('[name="height"]'), length = node.querySelector('[name="length"]'), linked = connections(side, sides);
        if (old[side]) { height.value = old[side].height; length.value = old[side].length; }
        node.querySelector('[data-end="left"]').innerHTML = endControl(side, 'left', linked.left, old[side]?.left);
        node.querySelector('[data-end="right"]').innerHTML = endControl(side, 'right', linked.right, old[side]?.right);
        const info = node.querySelector('[data-availability]');
        const update = () => { info.textContent = height.value === 'auto' ? 'Auto vurderer alle glass: 50 x 45, 50 x 70, 50 x 80, 70 x 50, 70 x 80, 80 x 50 og 80 x 70 cm.' : `Tilgjengelige glass (høyde x bredde): ${MODULES[height.value].map(width => `${height.value} x ${width} cm`).join(', ')}`; };
        height.addEventListener('change', update);
        update(); panels.appendChild(node);
      });
      renderCornerControls();
      panels.querySelectorAll('input,select').forEach(control => control.addEventListener('input', draw));
      draw();
    }

    function draw() {
      const sides = selected(), box = root.querySelector('[data-preview]');
      if (!sides.length) { box.innerHTML = '<p>Velg minst én side.</p>'; return; }
      box.innerHTML = render3D(sides);
    }

    picks.forEach(pick => pick.addEventListener('change', renderPanels));
    form.elements.topBoard.addEventListener('change', () => { renderCornerControls(); draw(); });
    form.addEventListener('reset', () => setTimeout(() => { picks.forEach((pick, index) => { pick.checked = index === 0; }); renderPanels(); root.querySelector('[data-results]').hidden = true; }, 0));
    form.addEventListener('submit', event => {
      event.preventDefault();
      const sides = selected(), error = root.querySelector('[data-error]');
      error.hidden = true;
      if (!sides.length) { error.textContent = 'Velg minst én side som skal beregnes.'; error.hidden = false; return; }
      const data = [];
      let invalid = false;
      const cornerSettings = {};
      root.querySelectorAll('[data-corner]').forEach(item => { cornerSettings[item.dataset.corner] = cornerEnd(item.dataset.corner); });
      function calculateActiveSide(side) {
        const panel = panels.querySelector(`[data-side="${side}"]`);
        if (!panel) return null;
        const length = Math.round(Number(panel.querySelector('[name="length"]').value) * 10), heightChoice = panel.querySelector('[name="height"]').value, linked = connections(side, sides);
        if (!length || length < 300) { invalid = true; return null; }
        const ends = {
          left: linked.left ? cornerSettings[cornerKey(side, 'left')] : panel.querySelector('[name="end-left"]').value,
          right: linked.right ? cornerSettings[cornerKey(side, 'right')] : panel.querySelector('[name="end-right"]').value
        };
        if (heightChoice === 'auto') {
          const auto = solveAuto(length, ends.left, ends.right, 0);
          return { side, length, height: auto.height, ends, result: auto.result, automatic: true };
        }
        const height = Number(heightChoice);
        return { side, length, height, ends, result: solve(length, height, ends.left, ends.right, 0), automatic: false };
      }
      sides.forEach(side => {
        const calculatedSide = calculateActiveSide(side);
        if (calculatedSide) data.push(calculatedSide);
      });
      if (invalid) { error.textContent = 'Fyll inn en gyldig total lengde på minst 30,00 cm for alle valgte sider.'; error.hidden = false; return; }
      show(data, cornerSettings);
      root.querySelector('[data-results]').scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    let printPortal = null;
    function closePrintView() {
      if (printPortal) printPortal.remove();
      printPortal = null;
      document.body.classList.remove('eg-is-printing');
    }
    function printResults() {
      closePrintView();
      const resultCopy = root.querySelector('[data-results]').cloneNode(true);
      resultCopy.hidden = false;
      printPortal = document.createElement('div');
      printPortal.className = 'eg eg-print-portal';
      printPortal.setAttribute('aria-hidden', 'true');
      printPortal.appendChild(resultCopy);
      document.body.appendChild(printPortal);
      document.body.classList.add('eg-is-printing');
      requestAnimationFrame(() => window.print());
    }
    root.querySelector('[data-print]').addEventListener('click', printResults);
    window.addEventListener('afterprint', closePrintView);

    function show(data, cornerSettings) {
      const results = root.querySelector('[data-results]'), cards = root.querySelector('[data-result-cards]');
      let totalGlass = 0;
      const glassCounts = {}, postCounts = {};
      const addPost = (type, height, count) => { const key = `${type}|${height}`; postCounts[key] = (postCounts[key] || 0) + count; };
      cards.innerHTML = data.map(d => {
        const result = d.result, count = result.modules.length;
        totalGlass += count;
        addPost('Standardstender', d.height, Math.max(0, count - 1));
        ['left', 'right'].forEach(position => { if (d.ends[position] === 'wall') addPost('Veggstender', d.height, 1); else if (d.ends[position] === 'standard') addPost('Standardstender', d.height, 1); });
        result.modules.forEach(width => { const key = `${d.height} x ${width}`; glassCounts[key] = (glassCounts[key] || 0) + 1; });
        const counts = {}; result.modules.forEach(width => { counts[width] = (counts[width] || 0) + 1; });
        const specialGlass = result.specialGlass && result.specialGlass.width > 0
          ? `<p class="eg__result-note"><strong>Spesialglass:</strong> Erstatt ett glass på ${result.specialGlass.replaces} mm med spesialbredde ${result.specialGlass.width} mm for å ta opp avviket.</p>`
          : '';
        return `<article class="eg__result-card"><h4>${LABELS[d.side]}</h4><div><strong>${count} glassmodul${count === 1 ? '' : 'er'}</strong><div class="eg__chips">${Object.entries(counts).map(([width, amount]) => `<span class="eg__chip">${amount} stk. ${d.height} x ${width} cm</span>`).join('')}</div><div class="eg__facts"><div class="eg__fact"><span>Total plass brukt</span><strong>${cm(result.used)} cm</strong></div><div class="eg__fact"><span>Resultat</span><strong>${fitText(result.rest)}</strong></div></div>${specialGlass}${diagram(d, 0)}</div></article>`;
      }).join('');
      const bySide = Object.fromEntries(data.map(item => [item.side, item]));
      const cornerType = setting => setting === 'corner-wall' ? 'Veggstender (hjørne)' : setting.endsWith('edge') ? 'Standardstender (hjørne, kant)' : 'Standardstender (hjørne, midt)';
      if (bySide.front && bySide.left) addPost(cornerType(cornerSettings.left), Math.max(bySide.front.height, bySide.left.height), 1);
      if (bySide.front && bySide.right) addPost(cornerType(cornerSettings.right), Math.max(bySide.front.height, bySide.right.height), 1);
      const hasSharedCorner = Boolean(bySide.front && (bySide.left || bySide.right));
      const cornerNote = hasSharedCorner ? '<p>Felles hjørnestendere er allerede slått sammen og ikke dobbelttelt.</p>' : '';
      root.querySelector('[data-total]').textContent = `${totalGlass} glass totalt`;
      root.querySelector('[data-print-date]').textContent = `Opprettet ${new Intl.DateTimeFormat('nb-NO', { dateStyle: 'long' }).format(new Date())}`;
      root.querySelector('[data-summary]').innerHTML = `<strong>Samlet materialforslag</strong><ul>${Object.entries(glassCounts).map(([measure, amount]) => `<li>${amount} stk. glass ${measure} cm</li>`).join('')}${Object.entries(postCounts).filter(([, amount]) => amount > 0).map(([key, amount]) => { const [type, height] = key.split('|'); return `<li>${amount} stk. ${type.toLowerCase()} ${height} cm</li>`; }).join('')}</ul>${cornerNote}`;
      root.querySelector('[data-preview]').innerHTML = render3D(data.map(item => item.side), data);
      results.hidden = false;
    }
    renderPanels();
  });
})();

